import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import TeamManagement from '../components/TeamManagement';
import FinancialsDashboard from '../components/FinancialsDashboard';
import StandardsLibrary from '../components/StandardsLibrary';
import RewardsManager from '../components/RewardsManager';
import ProjectsManager from '../components/ProjectsManager';
import ClientsManager from '../components/ClientsManager';
import BrandingSettings from '../components/BrandingSettings';
import { api, projectApi, clientApi, invoiceApi, teamApi } from '../services/api';

const AdminDashboard = ({ tab = 'overview' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tab);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    pendingReview: 0,
    revenue: { month: 0, year: 0 },
    team: { total: 0, active: 0 },
    unpaidInvoices: 0
  });

  // Modal states
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Sync activeTab with prop when route changes
  useEffect(() => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    // Fetch dashboard stats
    fetchStats();
  }, []);

  const fetchStats = async () => {
    // Mock data - would come from API
    setStats({
      totalProjects: 156,
      activeProjects: 12,
      pendingReview: 4,
      revenue: { month: 24500, year: 287000 },
      team: { total: 8, active: 6 },
      unpaidInvoices: 3
    });
  };

  // Handle tab change with URL navigation
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'overview') {
      navigate('/admin');
    } else {
      navigate(`/admin/${newTab}`);
    }
  };

  // Quick action handlers
  const handleNewProject = () => {
    setShowNewProjectModal(true);
  };

  const handleCreateInvoice = () => {
    setShowInvoiceModal(true);
  };

  const handleAddReward = () => {
    setShowRewardModal(true);
  };

  const handleInviteUser = () => {
    setShowInviteModal(true);
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <button
            className="btn icon"
            onClick={() => setShowNotifications(!showNotifications)}
            title="Notifications"
          >
            <Icon name="Bell" />
            <span className="notification-badge">3</span>
          </button>
          <button
            className="btn icon"
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
          >
            <Icon name="Settings" />
          </button>
        </div>
      </div>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <NotificationsDropdown onClose={() => setShowNotifications(false)} />
      )}

      {/* Settings Dropdown */}
      {showSettings && (
        <SettingsDropdown
          onClose={() => setShowSettings(false)}
          onNavigate={handleTabChange}
        />
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => handleTabChange('projects')}
        >
          Projects
        </button>
        <button
          className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => handleTabChange('clients')}
        >
          Clients
        </button>
        <button
          className={`tab ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => handleTabChange('team')}
        >
          Team
        </button>
        <button
          className={`tab ${activeTab === 'financials' ? 'active' : ''}`}
          onClick={() => handleTabChange('financials')}
        >
          Financials
        </button>
        <button
          className={`tab ${activeTab === 'standards' ? 'active' : ''}`}
          onClick={() => handleTabChange('standards')}
        >
          Standards
        </button>
        <button
          className={`tab ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => handleTabChange('rewards')}
        >
          Rewards
        </button>
        <button
          className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => handleTabChange('ai')}
        >
          AI Drafting
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <OverviewTab
            stats={stats}
            onNewProject={handleNewProject}
            onCreateInvoice={handleCreateInvoice}
            onAddReward={handleAddReward}
            onNavigate={handleTabChange}
          />
        )}
        {activeTab === 'projects' && (
          <ProjectsManager onNewProject={handleNewProject} />
        )}
        {activeTab === 'clients' && <ClientsManager />}
        {activeTab === 'team' && (
          <TeamManagement onInviteUser={handleInviteUser} />
        )}
        {activeTab === 'financials' && (
          <FinancialsDashboard onCreateInvoice={handleCreateInvoice} />
        )}
        {activeTab === 'standards' && <StandardsLibrary />}
        {activeTab === 'rewards' && (
          <RewardsManager onAddReward={handleAddReward} />
        )}
        {activeTab === 'ai' && <AIDraftingTab />}
        {activeTab === 'branding' && <BrandingSettings />}
        {activeTab === 'billing' && <BillingSettings />}
        {activeTab === 'users' && (
          <UsersSettings onInviteUser={handleInviteUser} />
        )}
        {activeTab === 'integrations' && <IntegrationsSettings />}
      </div>

      {/* Modals */}
      {showNewProjectModal && (
        <NewProjectModal
          onClose={() => setShowNewProjectModal(false)}
          onGoToClients={() => {
            setShowNewProjectModal(false);
            handleTabChange('clients');
          }}
        />
      )}
      {showInvoiceModal && (
        <CreateInvoiceModal onClose={() => setShowInvoiceModal(false)} />
      )}
      {showRewardModal && (
        <AddRewardModal onClose={() => setShowRewardModal(false)} />
      )}
      {showInviteModal && (
        <InviteUserModal onClose={() => setShowInviteModal(false)} />
      )}
    </div>
  );
};

// Notifications Dropdown
const NotificationsDropdown = ({ onClose }) => {
  const [notifications, setNotifications] = useState([
    { id: 1, type: 'project', message: 'New project submitted by ABC Roofing', time: '10 min ago', unread: true },
    { id: 2, type: 'payment', message: 'Invoice #INV-2024-0089 was paid', time: '1 hour ago', unread: true },
    { id: 3, type: 'team', message: 'Mike checked in for the day', time: '2 hours ago', unread: true },
    { id: 4, type: 'review', message: 'Drawing SD-A1 pending review', time: '3 hours ago', unread: false },
  ]);

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  };

  return (
    <div className="dropdown-overlay" onClick={onClose}>
      <div className="dropdown notifications-dropdown" onClick={e => e.stopPropagation()}>
        <div className="dropdown-header">
          <h3>Notifications</h3>
          <button className="btn text small" onClick={markAllRead}>Mark all as read</button>
        </div>
        <div className="dropdown-content">
          {notifications.map(notif => (
            <div key={notif.id} className={`notification-item ${notif.unread ? 'unread' : ''}`}>
              <span className="notif-icon">
                {notif.type === 'project' && 'Folder'}
                {notif.type === 'payment' && 'Dollar'}
                {notif.type === 'team' && 'User'}
                {notif.type === 'review' && 'Eye'}
              </span>
              <div className="notif-content">
                <p>{notif.message}</p>
                <span className="notif-time">{notif.time}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="dropdown-footer">
          <button className="btn text" disabled title="Coming soon">View All Notifications</button>
        </div>
      </div>
    </div>
  );
};

// Settings Dropdown
const SettingsDropdown = ({ onClose, onNavigate }) => {
  const settingsItems = [
    { id: 'branding', label: 'Branding', icon: 'Palette' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
  ];

  return (
    <div className="dropdown-overlay" onClick={onClose}>
      <div className="dropdown settings-dropdown" onClick={e => e.stopPropagation()}>
        <div className="dropdown-header">
          <h3>Settings</h3>
        </div>
        <div className="dropdown-content">
          {settingsItems.map(item => (
            <button
              key={item.id}
              className="settings-item"
              onClick={() => {
                onNavigate(item.id);
                onClose();
              }}
            >
              <span className="settings-icon"><Icon name={item.icon} /></span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// New Project Modal
const NewProjectModal = ({ onClose, onGoToClients }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    projectName: '',
    address: '',
    buildingType: '',
    roofType: '',
    squareFootage: '',
    dueDate: '',
    priority: 'normal',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadClients = async () => {
      const res = await clientApi.list();
      if (res.success && res.data && Array.isArray(res.data.clients)) {
        setClients(res.data.clients);
      }
    };
    loadClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const descriptionParts = [];
      if (formData.buildingType) descriptionParts.push(`Building type: ${formData.buildingType}`);
      if (formData.roofType) descriptionParts.push(`Roof type: ${formData.roofType}`);
      if (formData.squareFootage) descriptionParts.push(`Square footage: ${formData.squareFootage}`);

      const res = await projectApi.create({
        name: formData.projectName,
        client_id: Number(formData.clientId),
        address: formData.address,
        due_date: formData.dueDate || undefined,
        priority: formData.priority,
        notes: formData.notes,
        description: descriptionParts.join('; ')
      });

      if (res.success && res.data) {
        alert(`Project ${res.data.project_number} created successfully!`);
        onClose();
      } else {
        setError(res.error || 'Failed to create project. Please try again.');
      }
    } catch (err) {
      setError('Failed to create project. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Project</h2>
          <button className="btn icon" onClick={onClose}>X</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="form-group">
              <label>Client *</label>
              <select
                value={formData.clientId}
                onChange={e => setFormData({...formData, clientId: e.target.value})}
                required
              >
                <option value="">
                  {clients.length === 0 ? 'No clients available' : 'Select client...'}
                </option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              {clients.length === 0 && (
                <p className="form-hint">
                  No clients yet. Create a client in the Clients tab first.
                  {onGoToClients && (
                    <button type="button" className="btn text small" onClick={onGoToClients}>
                      Go to Clients
                    </button>
                  )}
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Project Name *</label>
              <input
                type="text"
                value={formData.projectName}
                onChange={e => setFormData({...formData, projectName: e.target.value})}
                placeholder="e.g., Walmart Distribution Center - Atlanta"
                required
              />
            </div>

            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={e => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main St, City, State 12345"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Building Type</label>
                <select
                  value={formData.buildingType}
                  onChange={e => setFormData({...formData, buildingType: e.target.value})}
                >
                  <option value="">Select...</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="retail">Retail</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </div>
              <div className="form-group">
                <label>Roof Type</label>
                <select
                  value={formData.roofType}
                  onChange={e => setFormData({...formData, roofType: e.target.value})}
                >
                  <option value="">Select...</option>
                  <option value="tpo">TPO</option>
                  <option value="epdm">EPDM</option>
                  <option value="pvc">PVC</option>
                  <option value="mod-bit">Modified Bitumen</option>
                  <option value="bur">Built-Up Roofing</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Square Footage</label>
                <input
                  type="number"
                  value={formData.squareFootage}
                  onChange={e => setFormData({...formData, squareFootage: e.target.value})}
                  placeholder="e.g., 50000"
                />
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={e => setFormData({...formData, dueDate: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="priority"
                    value="low"
                    checked={formData.priority === 'low'}
                    onChange={e => setFormData({...formData, priority: e.target.value})}
                  />
                  Low
                </label>
                <label>
                  <input
                    type="radio"
                    name="priority"
                    value="normal"
                    checked={formData.priority === 'normal'}
                    onChange={e => setFormData({...formData, priority: e.target.value})}
                  />
                  Normal
                </label>
                <label>
                  <input
                    type="radio"
                    name="priority"
                    value="high"
                    checked={formData.priority === 'high'}
                    onChange={e => setFormData({...formData, priority: e.target.value})}
                  />
                  High
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Invoice Modal
const CreateInvoiceModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    projectId: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    dueDate: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOptions = async () => {
      const [clientRes, projectRes] = await Promise.all([
        clientApi.list(),
        projectApi.list()
      ]);
      if (clientRes.success && clientRes.data && Array.isArray(clientRes.data.clients)) {
        setClients(clientRes.data.clients);
      }
      if (projectRes.success && projectRes.data && Array.isArray(projectRes.data.projects)) {
        setProjects(projectRes.data.projects);
      }
    };
    loadOptions();
  }, []);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Backend derives due_date from payment_terms (days from today)
      const dueMs = new Date(formData.dueDate).getTime() - Date.now();
      const paymentTerms = Math.max(1, Math.ceil(dueMs / 86400000));

      const res = await invoiceApi.create({
        client_id: Number(formData.clientId),
        project_id: formData.projectId ? Number(formData.projectId) : undefined,
        line_items: formData.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.quantity * item.rate
        })),
        payment_terms: paymentTerms,
        notes: formData.notes
      });

      if (res.success && res.data) {
        alert(`Invoice ${res.data.invoice_number} created successfully!`);
        onClose();
      } else {
        setError(res.error || 'Failed to create invoice. Please try again.');
      }
    } catch (err) {
      setError('Failed to create invoice. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Invoice</h2>
          <button className="btn icon" onClick={onClose}>X</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label>Client *</label>
                <select
                  value={formData.clientId}
                  onChange={e => setFormData({...formData, clientId: e.target.value})}
                  required
                >
                  <option value="">
                    {clients.length === 0 ? 'No clients available' : 'Select client...'}
                  </option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Project</label>
                <select
                  value={formData.projectId}
                  onChange={e => setFormData({...formData, projectId: e.target.value})}
                >
                  <option value="">
                    {projects.length === 0 ? 'No projects available' : 'Select project...'}
                  </option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.project_number} - {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Due Date *</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData({...formData, dueDate: e.target.value})}
                required
              />
            </div>

            <div className="invoice-items">
              <h4>Line Items</h4>
              {formData.items.map((item, index) => (
                <div key={index} className="invoice-item-row">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    className="item-description"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="item-qty"
                    min="1"
                  />
                  <input
                    type="number"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={e => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                    className="item-rate"
                    min="0"
                    step="0.01"
                  />
                  <span className="item-total">${(item.quantity * item.rate).toFixed(2)}</span>
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      className="btn icon small"
                      onClick={() => removeItem(index)}
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn secondary small" onClick={addItem}>
                + Add Item
              </button>
            </div>

            <div className="invoice-total">
              <span>Total:</span>
              <strong>${calculateTotal().toFixed(2)}</strong>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                placeholder="Payment terms, notes, etc."
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Reward Modal
const AddRewardModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    recipientId: '',
    points: '',
    reason: '',
    customMessage: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadTeam = async () => {
      const res = await teamApi.list();
      if (res.success && res.data && Array.isArray(res.data.members)) {
        setTeamMembers(res.data.members.map(m => ({
          id: String(m.id),
          name: m.role_title ? `${m.role_title} (#${m.id})` : `Member #${m.id}`
        })));
      }
    };
    loadTeam();
  }, []);

  const presetReasons = [
    { label: 'On-Time Delivery', points: 50 },
    { label: 'Quality Work - No Revisions', points: 100 },
    { label: 'Early Delivery', points: 75 },
    { label: 'Excellent Client Feedback', points: 100 },
    { label: 'Team Player Award', points: 50 },
    { label: 'Custom', points: 0 },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const reason = formData.customMessage
        ? `${formData.reason} - ${formData.customMessage}`
        : formData.reason;

      // Note: rewardApi.award targets /api/rewards/points, which does not
      // exist on the backend; the verified route is /api/rewards/award.
      const res = await api.post('/api/rewards/award', {
        member_id: Number(formData.recipientId),
        points: Number(formData.points),
        reason
      });

      if (res.success && res.data) {
        alert(`Successfully awarded ${formData.points} points! New balance: ${res.data.new_balance}`);
        onClose();
      } else {
        setError(res.error || 'Failed to award points. Please try again.');
      }
    } catch (err) {
      setError('Failed to award points. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Award Points</h2>
          <button className="btn icon" onClick={onClose}>X</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="form-group">
              <label>Team Member *</label>
              <select
                value={formData.recipientId}
                onChange={e => setFormData({...formData, recipientId: e.target.value})}
                required
              >
                <option value="">
                  {teamMembers.length === 0 ? 'No team members available' : 'Select team member...'}
                </option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Reason</label>
              <div className="preset-buttons">
                {presetReasons.map(preset => (
                  <button
                    key={preset.label}
                    type="button"
                    className={`preset-btn ${formData.reason === preset.label ? 'active' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      reason: preset.label,
                      points: preset.points > 0 ? preset.points.toString() : formData.points
                    })}
                  >
                    {preset.label}
                    {preset.points > 0 && <span className="preset-points">+{preset.points}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Points *</label>
              <input
                type="number"
                value={formData.points}
                onChange={e => setFormData({...formData, points: e.target.value})}
                placeholder="Enter points amount"
                min="1"
                max="500"
                required
              />
            </div>

            <div className="form-group">
              <label>Custom Message (optional)</label>
              <textarea
                value={formData.customMessage}
                onChange={e => setFormData({...formData, customMessage: e.target.value})}
                placeholder="Add a personal message..."
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? 'Awarding...' : 'Award Points'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invite User Modal
const InviteUserModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'drafter'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await api.post('/api/companies/current/invite', {
        email: formData.email,
        role: formData.role
      });

      if (res.success && res.data) {
        alert(res.data.message || `Invitation sent to ${formData.email}!`);
        onClose();
      } else {
        setError(res.error || 'Failed to send invitation. Please try again.');
      }
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite User</h2>
          <button className="btn icon" onClick={onClose}>X</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="user@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="Full name"
              />
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select
                value={formData.role}
                onChange={e => setFormData({...formData, role: e.target.value})}
                required
              >
                <option value="client">Client</option>
                <option value="drafter">Drafter</option>
                <option value="admin">Admin</option>
              </select>
              <p className="form-hint">
                {formData.role === 'client' && 'Clients can submit projects and view their drawings.'}
                {formData.role === 'drafter' && 'Drafters can work on assigned projects and log time.'}
                {formData.role === 'admin' && 'Admins have full access to all features.'}
              </p>
            </div>

            <p className="form-hint">
              An invitation email with a signup link will be sent to this address.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats, onNewProject, onCreateInvoice, onAddReward, onNavigate }) => {
  const dailyCheckins = [
    { name: 'Mike Rodriguez', status: 'checked_in', time: '8:15 AM', note: 'Working on Walmart project' },
    { name: 'Sarah Chen', status: 'checked_in', time: '8:30 AM', note: 'Finishing Amazon details' },
    { name: 'James Wilson', status: 'pending', time: '-', note: '-' },
    { name: 'Emily Davis', status: 'off', time: '-', note: 'PTO' },
  ];

  const urgentItems = [
    { type: 'project', message: 'PRJ-2024-0042 due in 2 days', priority: 'high' },
    { type: 'invoice', message: '3 invoices overdue', priority: 'medium' },
    { type: 'review', message: '4 drawings pending review', priority: 'low' },
  ];

  return (
    <div className="overview-tab">
      {/* Stats Grid */}
      <div className="stats-grid-admin">
        <div className="stat-card large clickable" onClick={() => onNavigate('projects')}>
          <div className="stat-icon"><Icon name="Folder" /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.activeProjects}</span>
            <span className="stat-label">Active Projects</span>
          </div>
          <div className="stat-trend up">+3 this week</div>
        </div>
        <div className="stat-card large clickable" onClick={() => onNavigate('projects')}>
          <div className="stat-icon"><Icon name="Eye" /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.pendingReview}</span>
            <span className="stat-label">Pending Review</span>
          </div>
        </div>
        <div className="stat-card large success clickable" onClick={() => onNavigate('financials')}>
          <div className="stat-icon"><Icon name="Dollar" /></div>
          <div className="stat-content">
            <span className="stat-value">${stats.revenue.month.toLocaleString()}</span>
            <span className="stat-label">Revenue (Month)</span>
          </div>
          <div className="stat-trend up">+12% vs last month</div>
        </div>
        <div className="stat-card large warning clickable" onClick={() => onNavigate('financials')}>
          <div className="stat-icon"><Icon name="Receipt" /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.unpaidInvoices}</span>
            <span className="stat-label">Unpaid Invoices</span>
          </div>
        </div>
      </div>

      <div className="overview-grid">
        {/* Daily Check-ins */}
        <div className="overview-section">
          <div className="section-header">
            <h3>Daily Check-ins</h3>
            <span className="badge">{dailyCheckins.filter(c => c.status === 'checked_in').length}/{dailyCheckins.length}</span>
          </div>
          <div className="checkin-list">
            {dailyCheckins.map((person, i) => (
              <div key={i} className={`checkin-item ${person.status}`}>
                <div className="avatar">{person.name.charAt(0)}</div>
                <div className="checkin-info">
                  <span className="name">{person.name}</span>
                  <span className="note">{person.note}</span>
                </div>
                <div className="checkin-status">
                  {person.status === 'checked_in' && <span className="badge success">{person.time}</span>}
                  {person.status === 'pending' && <span className="badge warning">Pending</span>}
                  {person.status === 'off' && <span className="badge neutral">Off</span>}
                </div>
              </div>
            ))}
          </div>
          <button className="btn secondary full-width" disabled title="Coming soon">
            Send Reminder to Pending
          </button>
        </div>

        {/* Urgent Items */}
        <div className="overview-section">
          <div className="section-header">
            <h3>Attention Needed</h3>
          </div>
          <div className="urgent-list">
            {urgentItems.map((item, i) => (
              <div key={i} className={`urgent-item ${item.priority}`}>
                <span className="urgent-icon">
                  {item.type === 'project' && 'Folder'}
                  {item.type === 'invoice' && 'CreditCard'}
                  {item.type === 'review' && 'Eye'}
                </span>
                <span className="urgent-message">{item.message}</span>
                <button className="btn small" onClick={() => {
                  if (item.type === 'project') onNavigate('projects');
                  if (item.type === 'invoice') onNavigate('financials');
                  if (item.type === 'review') onNavigate('projects');
                }}>View</button>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="overview-section">
          <div className="section-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions-grid">
            <button className="quick-action" onClick={onNewProject}>
              <span>Upload</span>
              <span>New Project</span>
            </button>
            <button className="quick-action" onClick={onCreateInvoice}>
              <span>Dollar</span>
              <span>Create Invoice</span>
            </button>
            <button className="quick-action" onClick={() => onNavigate('team')}>
              <span>Users</span>
              <span>Manage Team</span>
            </button>
            <button className="quick-action" onClick={onAddReward}>
              <span>Gift</span>
              <span>Send Reward</span>
            </button>
            <button className="quick-action" onClick={() => onNavigate('financials')}>
              <span>Chart</span>
              <span>Run Payroll</span>
            </button>
            <button className="quick-action" onClick={() => onNavigate('ai')}>
              <span>Bot</span>
              <span>AI Draft Project</span>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="overview-section wide">
          <div className="section-header">
            <h3>Recent Activity</h3>
            <button className="btn text" disabled title="Coming soon">View All</button>
          </div>
          <div className="activity-feed">
            <div className="activity-item">
              <span className="activity-time">10:32 AM</span>
              <span className="activity-icon"><Icon name="Check" /></span>
              <span className="activity-text">Mike completed drawing SD-A1 for PRJ-2024-0042</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">10:15 AM</span>
              <span className="activity-icon"><Icon name="Upload" /></span>
              <span className="activity-text">New project submitted by ABC Roofing</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">9:45 AM</span>
              <span className="activity-icon"><Icon name="CreditCard" /></span>
              <span className="activity-text">Invoice #INV-2024-0089 paid ($2,450)</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">9:30 AM</span>
              <span className="activity-icon"><Icon name="Gift" /></span>
              <span className="activity-text">Sarah redeemed $10 Wawa gift card</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Drafting Tab
const AIDraftingTab = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [aiStatus, setAiStatus] = useState('idle');
  const [progress, setProgress] = useState(0);

  const startAIDraft = () => {
    setAiStatus('processing');
    setProgress(0);

    // Simulate AI processing
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setAiStatus('complete');
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  return (
    <div className="ai-drafting-tab">
      <div className="ai-header">
        <h2>AI-Assisted Drafting</h2>
        <p>Let AI generate initial shop drawings from parsed documents</p>
      </div>

      <div className="ai-workflow">
        <div className="workflow-step">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Select Project</h4>
            <select
              value={selectedProject || ''}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">Choose a project...</option>
              <option value="PRJ-2024-0042">PRJ-2024-0042 - Walmart Distribution</option>
              <option value="PRJ-2024-0041">PRJ-2024-0041 - Amazon Fulfillment</option>
            </select>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Parse Documents</h4>
            <p>AI will extract assembly data, specs, and requirements</p>
            <div className="parse-status">
              <span className="status-item complete">Scope parsed</span>
              <span className="status-item complete">Specs parsed</span>
              <span className="status-item complete">Assembly letters parsed</span>
              <span className="status-item pending">Drawings analyzing...</span>
            </div>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-number">3</div>
          <div className="step-content">
            <h4>Generate Drawings</h4>
            <button
              className="btn primary large"
              onClick={startAIDraft}
              disabled={!selectedProject || aiStatus === 'processing'}
            >
              {aiStatus === 'idle' && 'Start AI Draft'}
              {aiStatus === 'processing' && 'Generating...'}
              {aiStatus === 'complete' && 'Complete!'}
            </button>

            {aiStatus !== 'idle' && (
              <div className="ai-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span>{progress}%</span>
              </div>
            )}
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-number">4</div>
          <div className="step-content">
            <h4>Human Review</h4>
            <p>Review and approve AI-generated drawings</p>
            {aiStatus === 'complete' && (
              <div className="generated-drawings">
                <div className="drawing-preview">
                  <span>SD-A1-ROOF-PLAN.dwg</span>
                  <button className="btn small primary" disabled title="Coming soon">Review</button>
                </div>
                <div className="drawing-preview">
                  <span>SD-A2-DETAILS.dwg</span>
                  <button className="btn small primary" disabled title="Coming soon">Review</button>
                </div>
                <div className="drawing-preview">
                  <span>SD-A3-SECTIONS.dwg</span>
                  <button className="btn small primary" disabled title="Coming soon">Review</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Stats */}
      <div className="ai-stats">
        <div className="stat-card">
          <span className="stat-value">47</span>
          <span className="stat-label">AI Drafts This Month</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">82%</span>
          <span className="stat-label">Approval Rate</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">3.2 hrs</span>
          <span className="stat-label">Avg Time Saved</span>
        </div>
      </div>
    </div>
  );
};

// Billing Settings Component
const BillingSettings = () => {
  return (
    <div className="settings-tab">
      <div className="settings-header">
        <h2>Billing and Subscription</h2>
        <p>Manage your subscription, payment methods, and invoices</p>
      </div>

      <div className="settings-section">
        <h3>Current Plan</h3>
        <div className="plan-card">
          <div className="plan-name">Professional</div>
          <div className="plan-price">$299/month</div>
          <div className="plan-features">
            <li>Unlimited projects</li>
            <li>10 team members</li>
            <li>AI drafting access</li>
            <li>Priority support</li>
          </div>
          <button className="btn primary" disabled title="Coming soon">Upgrade Plan</button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Payment Method</h3>
        <div className="payment-method">
          <span>Visa ending in 4242</span>
          <button className="btn secondary" disabled title="Coming soon">Update</button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Billing History</h3>
        <div className="billing-list">
          <div className="billing-item">
            <span>Jan 1, 2026</span>
            <span>$299.00</span>
            <span className="status paid">Paid</span>
            <button className="btn text" disabled title="Coming soon">Download</button>
          </div>
          <div className="billing-item">
            <span>Dec 1, 2025</span>
            <span>$299.00</span>
            <span className="status paid">Paid</span>
            <button className="btn text" disabled title="Coming soon">Download</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Settings Component
const UsersSettings = ({ onInviteUser }) => {
  const users = [
    { id: 1, name: 'John Admin', email: 'john@company.com', role: 'owner', status: 'active' },
    { id: 2, name: 'Sarah Manager', email: 'sarah@company.com', role: 'admin', status: 'active' },
    { id: 3, name: 'Mike Drafter', email: 'mike@company.com', role: 'drafter', status: 'active' },
  ];

  return (
    <div className="settings-tab">
      <div className="settings-header">
        <h2>User Management</h2>
        <p>Manage users and their permissions</p>
        <button className="btn primary" onClick={onInviteUser}>+ Invite User</button>
      </div>

      <div className="users-table">
        <div className="table-header">
          <span>User</span>
          <span>Role</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {users.map(user => (
          <div key={user.id} className="table-row">
            <div className="user-info">
              <div className="avatar">{user.name.charAt(0)}</div>
              <div>
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>
            </div>
            <span className={`role-badge ${user.role}`}>{user.role}</span>
            <span className={`status-badge ${user.status}`}>{user.status}</span>
            <div className="actions">
              <button className="btn text" disabled title="Coming soon">Edit</button>
              <button className="btn text danger" disabled title="Coming soon">Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Integrations Settings Component
const IntegrationsSettings = () => {
  const integrations = [
    { id: 'stripe', name: 'Stripe', description: 'Payment processing', connected: true, icon: 'CreditCard' },
    { id: 's3', name: 'AWS S3', description: 'File storage', connected: true, icon: 'Cloud' },
    { id: 'slack', name: 'Slack', description: 'Team notifications', connected: false, icon: 'Message' },
    { id: 'quickbooks', name: 'QuickBooks', description: 'Accounting sync', connected: false, icon: 'Chart' },
  ];

  return (
    <div className="settings-tab">
      <div className="settings-header">
        <h2>Integrations</h2>
        <p>Connect third-party services to enhance your workflow</p>
      </div>

      <div className="integrations-grid">
        {integrations.map(integration => (
          <div key={integration.id} className="integration-card">
            <div className="integration-icon"><Icon name={integration.icon} /></div>
            <div className="integration-info">
              <h4>{integration.name}</h4>
              <p>{integration.description}</p>
            </div>
            <button
              className={`btn ${integration.connected ? 'secondary' : 'primary'}`}
              disabled
              title="Coming soon"
            >
              {integration.connected ? 'Disconnect' : 'Connect'}
            </button>
            {integration.connected && <span className="connected-badge">Connected</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
